/* SAMSON MWITA: progressive enhancement for editorial motion.
   Content stays visible without this file, observers or animation support. */
(() => {
  if (window.smEditorialMotion) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const phone = window.matchMedia('(max-width: 777px)');
  let phoneViewport = phone.matches;
  const revealSelector = [
    '.sm-split__copy > *',
    '.sm-statement__inner',
    '.sm-image-feature__copy > *',
    '.sm-editorial-panels__copy > *',
    '.sm-collection-rail__header',
    '.sm-collection-rail__details',
    '.sm-newsletter__intro',
    '.sm-blog-hero__body > *',
    '.sm-blog-card article-card > .meta',
    '.sm-collection-story__copy',
    '.sm-house-entry :is(.sm-section-head, .sm-editorial-copy, .sm-code-card, .sm-quote, .sm-select-copy)',
    '.sm-house [data-sm-reveal]',
  ].join(',');
  const scanSelector = `#mainHeader,#mainContent,${revealSelector}`;
  const seen = new WeakSet();
  const pending = new Map();
  const animations = new Map();
  let observer;
  let header;
  let enabled = false;
  let refreshFrame = 0;

  function finishAnimations() {
    for (const animation of animations.keys()) {
      // Cancelling restores the visible, untransformed stylesheet state.
      animation.cancel();
    }
    animations.clear();
  }

  function play(element) {
    if (!enabled || !element.isConnected || seen.has(element)) return;
    // Images and their containers must never fade out when entering view.
    if (element.matches('img, picture, video, canvas') || element.querySelector('img, picture, video, canvas')) return;
    seen.add(element);
    const keyframes = [{ opacity: 0.9 }, { opacity: 1 }];
    try {
      const animation = element.animate(keyframes, {
        duration: 360,
        easing: 'cubic-bezier(.22, 1, .36, 1)',
        iterations: 1,
        fill: 'none',
      });
      animations.set(animation, { element });
      const release = () => animations.delete(animation);
      animation.addEventListener('finish', release, { once: true });
      animation.addEventListener('cancel', release, { once: true });
    } catch (_) {
      // Failed enhancement must never leave content hidden.
    }
  }

  function onIntersection(entries) {
    for (const entry of entries) {
      const item = pending.get(entry.target);
      if (!item) continue;
      if (!entry.target.isConnected) {
        observer.unobserve(entry.target);
        pending.delete(entry.target);
      } else if (entry.isIntersecting && entry.intersectionRatio > 0) {
        observer.unobserve(entry.target);
        pending.delete(entry.target);
        play(entry.target);
      }
    }
  }

  function collect(main, selector) {
    main.querySelectorAll(selector).forEach(element => {
      if (seen.has(element) || pending.has(element) || !element.getClientRects().length) return;
      // Never animate both a copy block and its children, or replay an entrance
      // on initial content, restored scroll positions or visible photographs.
      if (element.parentElement.closest(selector) || element.matches('img, picture, video, canvas') ||
          element.querySelector('img, picture, video, canvas') || element.getBoundingClientRect().top < window.innerHeight) {
        seen.add(element);
        return;
      }
      pending.set(element, true);
      observer.observe(element);
    });
  }

  function refresh() {
    refreshFrame = 0;
    header = document.getElementById('mainHeader');
    const main = document.getElementById('mainContent');

    const optedIn = header?.dataset.smSiteMotion !== 'false';
    enabled = optedIn && !reduced.matches && !window.Shopify?.designMode;
    const applyPreference = () => {
      const value = String(enabled);
      if (document.body.dataset.smMotion !== value) document.body.dataset.smMotion = value;
    };
    // Phone copy paints in its final state. Keep the global motion preference
    // for the emblem, but avoid scanning and animating every editorial block.
    if (phoneViewport || !enabled || !main || !('IntersectionObserver' in window) || !Element.prototype.animate) {
      observer?.disconnect();
      pending.clear();
      finishAnimations();
      applyPreference();
      return;
    }

    observer ||= new IntersectionObserver(onIntersection, { threshold: 0, rootMargin: '0px 0px 80px 0px' });
    for (const element of pending.keys()) {
      if (!element.isConnected) {
        observer.unobserve(element);
        pending.delete(element);
      }
    }
    for (const [animation, item] of animations) {
      if (!item.element.isConnected) {
        animation.cancel();
        animations.delete(animation);
      }
    }
    collect(main, revealSelector);
    // Complete geometry reads before changing a body attribute used by motion
    // styles. Mobile needs no viewport/layout read or editorial reveal scan.
    applyPreference();
  }

  function queueRefresh() {
    if (!refreshFrame) refreshFrame = window.requestAnimationFrame(refresh);
  }

  function start() {
    refresh();
    // Shopify editor replacement, fetched sections and infinite-scroll cards.
    new MutationObserver((records) => {
      if (phoneViewport) return;
      const relevant = records.some((record) =>
        [...record.addedNodes, ...record.removedNodes].some((node) =>
          node.nodeType === 1 && (node.matches(scanSelector) || node.querySelector(scanSelector))
        )
      );
      if (relevant) queueRefresh();
    }).observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', () => {
      // Mobile address bars and keyboards resize the height during ordinary
      // scrolling. IntersectionObserver already handles those visibility changes.
      if (phoneViewport) return;
      queueRefresh();
    }, { passive: true });
    phone.addEventListener('change', event => {
      phoneViewport = event.matches;
      queueRefresh();
    });
    window.addEventListener('pageshow', queueRefresh);
    window.addEventListener('pagehide', () => {
      finishAnimations();
      observer?.disconnect();
      pending.clear();
    });
    reduced.addEventListener('change', queueRefresh);
    document.addEventListener('shopify:section:load', queueRefresh);
    document.addEventListener('shopify:section:reorder', queueRefresh);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) finishAnimations();
    });
    document.addEventListener('focusin', (event) => {
      for (const [animation, item] of animations) {
        if (item.element.contains(event.target)) animation.finish();
      }
    });
  }

  window.smEditorialMotion = { refresh: queueRefresh };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
